import React, { useRef } from 'react';
import { Switch } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faCog, faMoon, faRightFromBracket, faUser } from '@fortawesome/free-solid-svg-icons';
import './SettingsMenu.css';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import type { LocalAuthUser } from '../../data/types';
import { useI18n } from '../../i18n/I18nContext';

// Props for the SettingsMenu component.
export interface SettingsMenuProps {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  currentVersion: string;
  currentUser: LocalAuthUser | null;
  darkMode: boolean;
  onToggleDarkMode: () => void;
  onOpenProfile: () => void;
  onLogout: () => void;
}

const SettingsMenu: React.FC<SettingsMenuProps> = ({ open, onToggle, onClose, currentVersion, currentUser, darkMode, onToggleDarkMode, onOpenProfile, onLogout }) => {
  const { t } = useI18n();
  const menuRef = useRef<HTMLDivElement>(null);

  useClickOutside(menuRef, onClose, open);
  useEscapeKey(onClose, open);

  return (
    <div className="settings-menu" ref={menuRef}>
      {currentUser?.displayName && (
        <div className="settings-greeting">
          <span>{t('header.hello')} </span>
          <button
            type="button"
            className="settings-greeting-name"
            onClick={() => {
              onOpenProfile();
              onClose();
            }}
          >
            {currentUser.displayName}
          </button>
        </div>
      )}
      <button
        type="button"
        className="settings-icon"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={e => {
          e.stopPropagation();
          onToggle();
        }}
      >
        <FontAwesomeIcon icon={faCog} />
      </button>
      {open && (
        <div className="dropdown-menu" style={{ right: 0, left: 'auto' }} role="menu">
          <div className="settings-item">
            <span className="settings-item-label">
              <span className="settings-item-icon"><FontAwesomeIcon icon={faCircleInfo} /></span>
              <span>{t('menus.version')}</span>
            </span>
            <span>{currentVersion}</span>
          </div>
          <div className="dropdown-divider" />
          <div className="settings-item switch-item">
            <span className="settings-item-label">
              <span className="settings-item-icon"><FontAwesomeIcon icon={faMoon} /></span>
              <span>{t('menus.darkMode')}</span>
            </span>
            <Switch
              checked={darkMode}
              onChange={() => {
                onToggleDarkMode();
                onClose();
              }}
              size="small"
            />
          </div>
          <div className="dropdown-divider" />
          <button
            type="button"
            className="settings-action"
            onClick={() => {
              onOpenProfile();
              onClose();
            }}
          >
            <span className="settings-item-icon"><FontAwesomeIcon icon={faUser} /></span>
            <span>{t('menus.profile')}</span>
          </button>
          <button
            type="button"
            className="settings-action"
            onClick={() => {
              onLogout();
              onClose();
            }}
          >
            <span className="settings-item-icon"><FontAwesomeIcon icon={faRightFromBracket} /></span>
            <span>{t('menus.logout')}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default SettingsMenu;
